// @ts-nocheck
import { useState, useEffect } from 'react'
import { X, Save, AlertTriangle } from 'lucide-react'
import { useApi } from '../hooks/useApi'

interface IvrPhoneNumberSettingsProps {
  phoneNumber: { phoneNumberId: string; phoneNumber: string; name: string }
  onClose: () => void
}

export default function IvrPhoneNumberSettings({ phoneNumber, onClose }: IvrPhoneNumberSettingsProps) {
  const api = useApi()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIvrMenuId, setSelectedIvrMenuId] = useState<string>('')
  const [ivrMenus, setIvrMenus] = useState<Array<{ ivrMenuId: string; name: string }>>([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const [ivrConfig, menusData] = await Promise.all([
          api.get(`/api/phone-numbers/${phoneNumber.phoneNumberId}/ivr`),
          api.get('/api/ivr-menus'),
        ])

        setSelectedIvrMenuId(ivrConfig.ivrMenuId ?? '')
        setIvrMenus(menusData.items ?? [])
      } catch (err) {
        console.error('IVR設定取得エラー:', err)
        setError('IVR設定の取得に失敗しました')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [phoneNumber.phoneNumberId])

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      await api.put(`/api/phone-numbers/${phoneNumber.phoneNumberId}/ivr`, {
        ivrMenuId: selectedIvrMenuId || null,
      })

      onClose()
    } catch (err) {
      console.error('IVR設定保存エラー:', err)
      setError('IVR設定の保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        {/* ヘッダー */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            IVR設定 — {phoneNumber.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* エラーメッセージ */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* IVRメニュー選択 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                IVRメニュー
              </label>
              <select
                value={selectedIvrMenuId}
                onChange={(e) => setSelectedIvrMenuId(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">なし（IVR無効）</option>
                {ivrMenus.map((menu) => (
                  <option key={menu.ivrMenuId} value={menu.ivrMenuId}>
                    {menu.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 注意事項 */}
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                IVRが有効な場合、ウェルカムメッセージ設定は使用されません
              </p>
            </div>
          </div>
        )}

        {/* アクションボタン */}
        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                保存中...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                保存
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
